import { IconButton, IconButtonType, Spinner } from "@whitespace/whitespace-ui";
import {
  Card, Col, Row, Badge
} from "react-bootstrap";
import { useStateMachine } from "little-state-machine";
import { useNavigate } from "react-router-dom";
import {
  DetailRow,
  ColumnDirective,
  ColumnsDirective,
  Filter,
  FilterSettingsModel,
  GridComponent,
  Inject,
  PageSettingsModel,
  Resize,
  Sort,
  Page as Paging
} from "@syncfusion/ej2-react-grids";
import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";
import StaffOnSiteProps from "interfaces/SiteDiary/StaffOnSite/StaffOnSiteProps";
import { parseUsingParseInt } from "helpers/stringUtils";
import { makeGridAACompliant } from "modules/SyncfusionAAFixer";
import { convertToDateFromLocalDate } from "helpers/DateUtils";
import { useState, useEffect } from "react";
import { RolesEnum } from "modules/rolesEnum";
import DetailsTemplate from "./Templates/DetailsTemplate";
import EditTemplate from "./Templates/EditTemplate";

import "./StaffOnSite.scss";

const StaffOnSite = ({
  staffOnSite,
  hwrcSiteId,
  diaryDate,
  diarySaved
}: StaffOnSiteProps) => {
  const now = new Date();
  const addUrl = `${RoutePathsEnum.SiteDiaryAddStaffOnSite}${hwrcSiteId && diaryDate
    ? `/${hwrcSiteId}/${diaryDate}` : ""}`;

  const navigate = useNavigate();
  const { state } = useStateMachine();
  const pageSize: number = parseUsingParseInt(
    process.env.REACT_APP_DIARY_CARD_PAGE_SIZE
  );
  const pageSettings: PageSettingsModel = {
    pageSize,
    pageSizes: [5, 10, 20, 50, "All"]
  };

  const filterSettings: FilterSettingsModel = { type: "Excel" };
  let grid: GridComponent | undefined;

  const detailsTemplateInstance = DetailsTemplate;
  const editTemplateInstance = EditTemplate;

  const [renderingComplete, setRenderingComplete] = useState(false);
  const [isGridRendered, setIsGridRendered] = useState(false);
  const [showSpinner, setShowSpinner] = useState<boolean>(true);

  useEffect(() => {
    if (isGridRendered && renderingComplete) {
      setShowSpinner(false);
    }
  }, [isGridRendered, renderingComplete, setShowSpinner]);

  const gridCreated = () => {
    setIsGridRendered(true);
  };

  const rowDataBound = (args: any) => {
    const expandIcon = args.row.querySelector(".e-detailrowcollapse");
    if (args.data.childData.length === 0 && expandIcon) {
      expandIcon.style.visibility = "hidden";
    }
  };

  const detailTemplate = (props: any) => {
    const rowData = props;
    if (rowData && rowData.childData && rowData.childData.length > 0) {
      return (
        <GridComponent dataSource={rowData.childData}>
          <ColumnsDirective>
            <ColumnDirective
              field="notes"
              headerText="Notes"
              textAlign="Left"
              width="100%"
            />
          </ColumnsDirective>
        </GridComponent>
      );
    }
    return null;
  };

  const canAmendDiary = diarySaved
    && !(
      diaryDate
      && convertToDateFromLocalDate(diaryDate).toLocaleDateString()
        !== now.toLocaleDateString()
      && state.userRole === RolesEnum.Site
    );

  return (
    <Card>
      <Card.Header>
        <Row>
          <Col className="header">
            <h3 data-testid="staff-on-site">Staff On Site</h3>
            <Spinner
              animation="grow"
              visible={showSpinner}
              spinnerText="Loading"
              variant="info"
            />
          </Col>
          <Col className="right-pane">
            {canAmendDiary && (
              <IconButton
                data-testid="add-staff-on-site"
                className="me-2"
                iconType={IconButtonType.Add}
                onClick={() => navigate(addUrl)}
              >
                Add
              </IconButton>
            )}
            <Badge data-testid="staff-on-site-count">
              {staffOnSite && staffOnSite.length}
            </Badge>
          </Col>
        </Row>
      </Card.Header>
      <Card.Body className="no-padding">
        <Row className="mb-2 ms-2 mt-2 me-2 w-auto">
          <Col>
            <GridComponent
              ref={(g: GridComponent) => {
                grid = g;
              }}
              id="gridStaffOnSite"
              data-testid="gridContainerStatuses"
              allowPaging
              allowResizing
              pageSettings={pageSettings}
              filterSettings={filterSettings}
              allowSelection={false}
              queryString=""
              dataSource={diarySaved && staffOnSite}
              actionComplete={() => {
                makeGridAACompliant(grid!, () => {
                  setRenderingComplete(true);
                });
              }}
              loadingIndicator={{ indicatorType: "Shimmer" }}
              type=""
              detailTemplate={detailTemplate}
              rowDataBound={rowDataBound}
              created={gridCreated}
            >
              <ColumnsDirective>
                <ColumnDirective
                  field="id"
                  headerText="id"
                  visible={false}
                  isPrimaryKey
                />
                <ColumnDirective field="name" headerText="Name" />
                <ColumnDirective
                  field="timeIn"
                  data-testid="timeIn"
                  format="HH:mm"
                  headerText="Time In"
                  type="datetime"
                  width={90}
                />
                <ColumnDirective
                  field="timeOut"
                  data-testid="timeOut"
                  format="HH:mm"
                  headerText="Time Out"
                  width={90}
                  type="datetime"
                />
                <ColumnDirective
                  field="id"
                  data-testid="details"
                  headerText="Details"
                  template={detailsTemplateInstance}
                />
                {canAmendDiary
                && (
                  <ColumnDirective
                    allowSorting={false}
                    data-testid="edit"
                    allowFiltering={false}
                    template={editTemplateInstance}
                    width={28}
                  />
                )}
              </ColumnsDirective>
              <Inject services={[Filter, Paging, Sort, Resize, DetailRow]} />
            </GridComponent>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default StaffOnSite;
